// ============================================================
// THE AMORPHOUS MASS'S CONDITIONS (plan body-map S2; the goo study, ruled
// 2026-10-09). The mass moves by its own body (engine/plate/gooLive): its
// conditions change that body (a sag, a boil, bands that cinch it, drops
// shed) and add what stands round it - and, as on the figures, take layers
// of their own over it: Haywire, the hallucination, the stun's ghosts, the
// fire's shimmer, Pinned's flicker, the state's dimming, Deafened's muffle.
//
// What moves is a SCENE (./scene): each element's key, tag and attributes,
// built from the body at a moment, drawn once and written by the clock.
// ============================================================

import type { ReactNode } from 'react';
import clsx from '../../../utils/clsx';
import type { PlateState } from '../../../engine/plate/plateState';
import {
  FOOT,
  fireWisps,
  gooBubbles,
  leakDrops,
  stasisField,
  voicePulse,
  type GooBody,
  type GooCondition,
  type LeakDrop,
} from '../../../engine/plate/gooLive';
import { pulledDrop, type Drop } from '../../../engine/plate/goo';
import type { Spark } from '../../../engine/plate/gooHits';
import { node, type SceneNode } from './scene';
import fx from '../../../styles/PlateFx.module.css';
import {
  filterOf,
  heatLayer,
  hlLayer,
  hwLayer,
  looksStyle,
  pinLayer,
  Stack,
  stunLayer,
  WholeBodyDefs,
  type Looks,
} from './conditions';

/** The goo study's filters run at half the figures' distances: its frame is a quarter of theirs, its mass larger. */
const GOO_K = 0.5;

/** The condition the body reads, from the plate's state and the eased weights of Dying and Dead. */
export function gooConditionOf(s: PlateState, dying: number, dead: number): GooCondition {
  const { fatigue, unconscious, paralysed, immob, fire, snared, stun } = s;
  return { fatigue, unconscious, paralysed, immob, fire, snared, stun, dying, dead };
}

/** A lit sphere: its rim, the bright arc on the side the light comes from (top-left), and its glint. */
function sphere(key: string, x: number, y: number, rx: number, ry: number): SceneNode[] {
  const [a0, a1] = [-2.6, -1.0];
  const at = (a: number) => `${x + rx * 0.78 * Math.cos(a)} ${y + ry * 0.78 * Math.sin(a)}`;
  return [
    node(`${key}r`, 'ellipse', { cx: x, cy: y, rx, ry }, fx.gooRim),
    node(`${key}l`, 'path', { d: `M${at(a0)} A${rx * 0.78} ${ry * 0.78} 0 0 1 ${at(a1)}` }, fx.gooLit),
    node(`${key}d`, 'circle', { cx: x - rx * 0.38, cy: y - ry * 0.42, r: Math.max(0.8, rx * 0.12) }, fx.gooGlint),
  ];
}

/** Blood Loss: each falling drop, a lit sphere stretching as it falls, and its splash in the ring. */
function leakNodes(drops: LeakDrop[]): SceneNode[] {
  return drops.flatMap((d, k) => [
    node(`leak${k}`, 'g', { opacity: d.opacity }, undefined, sphere(`leak${k}`, d.x, d.y, d.r, d.ry)),
    node(
      `splash${k}`,
      'ellipse',
      { cx: d.x, cy: FOOT, rx: d.splash, ry: d.splash * 0.2, opacity: d.splashOpacity },
      fx.gooRim,
    ),
  ]);
}

/** Immobilised: the stasis field's walls, its cap and the rings that scan it. */
function stasisNodes(t: number, body: GooBody): SceneNode[] {
  const f = stasisField(t, body);
  return [
    node(
      'fieldWalls',
      'path',
      { d: `M${f.cx - f.R} ${f.foot} V${f.top} M${f.cx + f.R} ${f.foot} V${f.top}` },
      fx.gooField,
    ),
    node('fieldCap', 'ellipse', { cx: f.cx, cy: f.top, rx: f.R, ry: f.R * 0.17 }, fx.gooField),
    ...f.scans.map((s, k) =>
      node(`fieldScan${k}`, 'ellipse', { cx: f.cx, cy: s.y, rx: f.R, ry: f.R * 0.17, opacity: s.opacity }, fx.gooField),
    ),
  ];
}

/** Snared: each band round the mass - its back half thin and dim, its front strong, brightening as it strains. */
function bandNodes(body: GooBody): SceneNode[] {
  return (body.bands ?? []).flatMap((b, k) => {
    const ry = b.rx * 0.2;
    const arc = (sweep: number) => `M${body.cx - b.rx} ${b.y} A${b.rx} ${ry} 0 0 ${sweep} ${body.cx + b.rx} ${b.y}`;
    return [
      node(`band${k}b`, 'path', { d: arc(1) }, clsx(fx.gooBand, fx.gooBandBack)),
      node(`band${k}f`, 'path', { d: arc(0), opacity: 0.6 + 0.4 * b.strain }, fx.gooBand),
    ];
  });
}

/** The bubbles rising through the core: lit spheres, all but out when Blinded. */
function bubbleNodes(t: number, body: GooBody, s: PlateState, c: GooCondition): SceneNode[] {
  return gooBubbles(t, body, c, s.blind).map((b, k) =>
    node(`bubble${k}`, 'g', { opacity: b.opacity }, undefined, sphere(`bubble${k}`, b.x, b.y, b.r, b.r)),
  );
}

/** Blood Loss also drains the mass from the top: the ground laid over it, down to an edge that falls and restarts. */
function drainNode(t: number, level: number): SceneNode {
  const [lo, hi, rate] = level === 2 ? [0.42, 0.78, 0.28] : [0.3, 0.55, 0.14];
  const edge = lo + (hi - lo) * ((t * rate) % 1);
  return node('drain', 'rect', { x: -200, y: 0, width: 1000, height: edge * 760 }, fx.gooDrain);
}

/** Everything that moves with the mass, at a moment: the bubbles always, and what its conditions add. */
export function gooScene(body: GooBody, t: number, s: PlateState, c: GooCondition): SceneNode[] {
  const voice = voicePulse(t, body);
  return [
    ...bubbleNodes(t, body, s, c),
    ...(s.fire
      ? fireWisps(t, body).map((w, k) =>
          node(`wisp${k}`, 'circle', { cx: w.x, cy: w.y, r: w.r, opacity: w.opacity }, fx.gooWisp),
        )
      : []),
    ...(s.bloodLoss ? [drainNode(t, s.bloodLoss), ...leakNodes(leakDrops(t, body, s.bloodLoss))] : []),
    ...(s.immob ? stasisNodes(t, body) : []),
    ...bandNodes(body),
    ...(s.mute
      ? [node('voice', 'circle', { cx: body.cx, cy: body.cy, r: voice.r, opacity: voice.opacity }, fx.gooVoice)]
      : []),
  ];
}

/**
 * One layer of the mass's drops (`data-drop` its index): each pulled toward the mass's middle (its first core unless
 * given - a torn mass's middle is between its parts) and shrunk for that layer (`pulledDrop`).
 */
export function dropNodes(
  layer: string,
  drops: readonly Drop[],
  pull: number,
  size: number,
  middle: Drop = drops[0],
): SceneNode[] {
  return drops.map((d, i) => {
    const [cx, cy, r] = pulledDrop(d, middle, pull, size);
    return node(`${layer}d${i}`, 'circle', { cx, cy, r, fill: 'currentColor', 'data-drop': i });
  });
}

/** Energy's steam off a struck mass: its wisps, drawn unseen and written by the clock (engine/plate/gooHits). */
export const sparkNodes = (sparks: readonly Spark[]): SceneNode[] =>
  sparks.map((p, k) => node(`spark${k}`, 'circle', { cx: p.x, cy: p.y, r: p.r, opacity: p.opacity }, fx.gooSpark));

/** The mass's colour by its conditions (`--goo`, which its filters flood with): fire, ice, held, calm, dead. */
export function gooColourClass(s: PlateState) {
  if (s.dead) return fx.gooDead;
  if (s.fire) return fx.gooFire;
  if (s.paralysed) return fx.gooIcy;
  if (s.immob) return fx.gooHeld;
  return s.unaware ? fx.gooCalm : undefined;
}

/** The mass's look by its state: Unconscious and Immobilised dim it, Unaware cools it. */
const GOO_LOOKS: Looks = [
  ['unconscious', '', 0.5],
  ['immob', '', 0.75],
  ['unaware', 'saturate(.6)', 1],
];

/** The filters the mass's conditions use, at the goo's scale (its muffle a lighter blur). */
export const GooConditionDefs = ({ u, s }: { u: string; s: PlateState }) => (
  <WholeBodyDefs u={u} s={s} k={GOO_K} deafBlur={5} />
);

/** The layers round the mass, outside in, one per condition, so any mix stacks. */
export function GooLayers({ u, s, children }: { u: string; s: PlateState; children: ReactNode }) {
  const layers = [
    hwLayer(u, s),
    hlLayer(u, s),
    stunLayer(u, s),
    heatLayer(u, s),
    pinLayer(s),
    { style: looksStyle(s, GOO_LOOKS) },
    { className: clsx(s.deaf && fx.deaf), filter: filterOf(u, s.deaf, 'deaf') },
  ];
  return <Stack layers={layers}>{children}</Stack>;
}
